import { skipToken } from '@reduxjs/toolkit/query/react';
import { isCrucibleAccessLocked, isCrucibleSaturationLimited, type CharacterCommission } from '@sorrem/shared';
import { useGetCharacterCommissionsQuery } from '@sorrem-client/ui/store';
import { useCurrentCharacterArgs } from './use-current-character-id';

export interface CharacterCommissionsState {
    /** The character these commissions belong to, or null before characters load. */
    characterId: string | null;
    /** That character's commissions, oldest first. */
    commissions: readonly CharacterCommission[];
    isLoading: boolean;
    /**
     * Whether the Crucibles listing is still locked: the character hasn't
     * completed the intro commission. False while loading, so nothing flashes
     * locked for characters who aren't.
     */
    isCruciblesLocked: boolean;
    /**
     * Whether the character may only create Faint Crucibles: the Mill Road
     * commission isn't complete yet. False while loading; the server refuses
     * anything else regardless.
     */
    isSaturationLimited: boolean;
}

const NO_COMMISSIONS: readonly CharacterCommission[] = [];

/**
 * The current character's commissions (see docs/gameplay/game-intro.md), for
 * the character `useCurrentCharacterId` picks. Hooks that need only that
 * character's id should use `useCurrentCharacterId`: this one refetches the
 * commissions on every mount.
 */
export function useCharacterCommissions(): CharacterCommissionsState {
    const args = useCurrentCharacterArgs();
    // Refetch on mount: a run can complete a commission while this is cached.
    const { data, isLoading } = useGetCharacterCommissionsQuery(args, { refetchOnMountOrArgChange: true });

    const commissions = data ?? NO_COMMISSIONS;
    return {
        characterId: args === skipToken ? null : args.characterId,
        commissions,
        isLoading,
        isCruciblesLocked: isCrucibleAccessLocked(commissions),
        isSaturationLimited: isCrucibleSaturationLimited(commissions),
    };
}
